import React, { useState } from 'react';
import { useEffect } from 'react';
import { documentConfigAPI } from '../../services/documentConfigAPI';
import { toast } from 'react-hot-toast';

const DocumentAccordion = ({ 
  documentTypes, 
  openAccordion, 
  setOpenAccordion,
  user,
  onEditDocument,
  onDeleteDocument,
  onUpdateField, // Prop para actualizar campos inline  
  onDeleteField,
  onRefreshData // Nueva prop para refrescar datos sin recargar la página
}) => {
  // Estado para edición inline
  const [editingField, setEditingField] = useState(null);
  const [tempFieldData, setTempFieldData] = useState({});
  const [savingField, setSavingField] = useState(false);
  const [addingNewField, setAddingNewField] = useState(null); // ID del documento al que se está agregando campo

  // Helper para obtener el siguiente orden disponible
  const getNextAvailableOrder = (documento, currentFieldId = null) => {
    const existingOrders = documento.campos
      .filter(campo => campo.id !== currentFieldId)
      .map(campo => campo.orden)
      .sort((a, b) => a - b);
    
    // Encontrar el primer número disponible
    for (let i = 1; i <= existingOrders.length + 1; i++) {
      if (!existingOrders.includes(i)) {
        return i;
      }
    }
    return existingOrders.length + 1;
  };

  // Iniciar edición inline
  const startInlineEdit = (campo) => {
    setEditingField(campo.id);
    setTempFieldData({
      orden: campo.orden,
      nombre: campo.nombre,
      especificacion: campo.especificacion || ''
    });
  };

  // Iniciar creación de nuevo campo
  const startAddingField = (documento) => {
    // Cancelar cualquier edición en curso
    setEditingField(null);
    setAddingNewField(documento.id);
    
    // Calcular el siguiente orden disponible
    const nextOrder = getNextAvailableOrder(documento);
    
    setTempFieldData({
      orden: nextOrder,
      nombre: '',
      especificacion: ''
    });
    console.log('[DocumentAccordion] startAddingField', { documentoId: documento.id, nextOrder });
  };

  // Debug: log state changes to trace why the new-row might not render
  useEffect(() => {
    console.log('[DocumentAccordion] state', {
      addingNewField,
      editingField,
      tempFieldData
    });
  }, [addingNewField, editingField, tempFieldData]);

  // Cancelar edición
  const cancelInlineEdit = () => {
    setEditingField(null);
    setAddingNewField(null);
    setTempFieldData({});
  };

  // Guardar cambios inline (edición)
  const saveInlineEdit = async (campoId) => {
    if (!tempFieldData.nombre.trim()) {
      toast.error('El nombre del campo es obligatorio');
      return;
    }

    // Buscar el documento que contiene este campo
    const documento = documentTypes.find(doc => 
      doc.campos.some(campo => campo.id === campoId)
    );
    
    if (documento) {
      // Verificar si hay conflicto de orden con otros campos (excluyendo el actual)
      const existeOrden = documento.campos.some(campo => 
        campo.id !== campoId && campo.orden === tempFieldData.orden
      );
      
      if (existeOrden) {
        toast.error(`Ya existe otro campo con el orden ${tempFieldData.orden} en este documento`);
        return;
      }
    }

    setSavingField(true);
    try {
      await onUpdateField(campoId, tempFieldData);
      setEditingField(null);
      setTempFieldData({});
    } catch (error) {
      console.error('Error al actualizar campo:', error);
      toast.error(error.message || 'Error al actualizar el campo');
    } finally {
      setSavingField(false);
    }
  };

  // Guardar nuevo campo
  const saveNewField = async (documentoId) => {
    if (!tempFieldData.nombre.trim()) {
      toast.error('El nombre del campo es obligatorio');
      return;
    }

    // Buscar el documento
    const documento = documentTypes.find(doc => doc.id === documentoId);
    
    if (documento) {
      // Verificar si hay conflicto de orden
      const existeOrden = documento.campos.some(campo => 
        campo.orden === tempFieldData.orden
      );
      
      if (existeOrden) {
        toast.error(`Ya existe un campo con el orden ${tempFieldData.orden} en este documento`);
        return;
      }
    }

    setSavingField(true);
    try {
      const fieldData = {
        orden: tempFieldData.orden,
        nombre: tempFieldData.nombre,
        especificacion: tempFieldData.especificacion || ""
      };

      await documentConfigAPI.createFieldByRole(documentoId, fieldData, user?.role);
      
      // Resetear estado
      setAddingNewField(null);
      setTempFieldData({});
      
      // Refrescar datos manteniendo el acordeón abierto
      if (onRefreshData) {
        await onRefreshData();
      }
      
      toast.success('Campo agregado exitosamente');
    } catch (error) {
      console.error('Error al agregar campo:', error);
      toast.error(error.message || 'Error al agregar el campo');
    } finally {
      setSavingField(false);
    }
  };

  // Manejar teclas (Enter para guardar, Escape para cancelar)
  const handleKeyDown = (e, campoId, isNewField = false) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isNewField) {
        saveNewField(campoId); // En este caso campoId es documentoId
      } else {
        saveInlineEdit(campoId);
      }
    } else if (e.key === 'Escape') {
      cancelInlineEdit();
    }
  };
  return (
    <div className="accordion" id="documentAccordion">
      {documentTypes.map((doc) => (
        <div key={doc.id} className="accordion-item">
          <h2 className="accordion-header">
            <button 
              className={`accordion-button ${openAccordion === doc.id ? '' : 'collapsed'} d-flex justify-content-between align-items-center`}
              type="button" 
              data-bs-toggle="collapse" 
              data-bs-target={`#collapse${doc.id}`}
              onClick={() => setOpenAccordion(openAccordion === doc.id ? null : doc.id)}
            >
              <div className="flex-grow-1">
                <strong>{doc.nombre}</strong>
                <div className="small text-muted">{doc.descripcion}</div>
                <div className="mt-1">
                  <span className="badge bg-secondary ms-0 me-2">{doc.campos.length} campos</span>
                  {user?.role === 'admin' ? (
                    <span className="badge bg-info">Plantilla Pública</span>
                  ) : (
                    <span className="badge bg-success">Documento Personal</span>
                  )}
                </div>
              </div>
            </button>
          </h2>
          <div 
            id={`collapse${doc.id}`} 
            className={`accordion-collapse collapse ${openAccordion === doc.id ? 'show' : ''}`}
            data-bs-parent="#documentAccordion"
          >
            <div className="accordion-body">
              {/* Botones de acción del documento */}
              <div className="d-flex gap-2 mb-3">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => onEditDocument(doc)}
                >
                  <i className="bi bi-pencil me-1"></i>
                  {user?.role === 'admin' ? 'Editar Plantilla' : 'Editar Documento'}
                </button>
                <button 
                  className="btn btn-sm btn-outline-success"
                  onClick={() => startAddingField(doc)}
                >
                  <i className="bi bi-plus me-1"></i>
                  Agregar Campo
                </button>
                <button 
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => onDeleteDocument(doc.id)}
                >
                  <i className="bi bi-trash me-1"></i>
                  Eliminar Documento
                </button>
              </div>

              {/* Lista de campos (siempre renderizamos la tabla para permitir agregar incluso cuando no haya campos) */}
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th width="80">Orden</th>
                      <th>Nombre del Campo</th>
                      <th>Especificación</th>
                      <th width="120">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.campos.length === 0 ? (
                      <tr className="text-muted">
                        <td colSpan={4}>
                          {user?.role === 'admin' 
                            ? 'Esta plantilla no tiene campos configurados. Use "Agregar Campo" para crear uno.'
                            : 'Este documento no tiene campos configurados. Use "Agregar Campo" para crear uno.'
                          }
                        </td>
                      </tr>
                    ) : (
                      doc.campos.sort((a, b) => a.orden - b.orden).map(campo => (
                        <tr 
                          key={campo.id} 
                          className={editingField === campo.id ? 'table-warning' : 'editable-row'}
                          onDoubleClick={() => editingField !== campo.id && startInlineEdit(campo)}
                          style={{ cursor: editingField !== campo.id ? 'pointer' : 'default' }}
                          title={editingField !== campo.id ? 'Doble clic para editar' : ''}
                        >
                          {editingField === campo.id ? (
                            // Modo edición inline
                            <>
                              <td>
                                <input
                                  type="number"
                                  className={`form-control form-control-sm ${
                                    doc.campos.some(c => c.id !== campo.id && c.orden === tempFieldData.orden) 
                                      ? 'is-invalid' : ''
                                  }`}
                                  value={tempFieldData.orden}
                                  onChange={(e) => setTempFieldData({...tempFieldData, orden: parseInt(e.target.value) || 1})}
                                  onKeyDown={(e) => handleKeyDown(e, campo.id)}
                                  min="1"
                                  max="99"
                                  title={`Orden actual: ${campo.orden}. Órdenes ocupados: ${doc.campos.filter(c => c.id !== campo.id).map(c => c.orden).sort((a,b) => a-b).join(', ')}`}
                                />
                                {doc.campos.some(c => c.id !== campo.id && c.orden === tempFieldData.orden) && (
                                  <div className="invalid-feedback">
                                    Orden {tempFieldData.orden} ya está en uso
                                  </div>
                                )}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={tempFieldData.nombre}
                                  onChange={(e) => setTempFieldData({...tempFieldData, nombre: e.target.value})}
                                  onKeyDown={(e) => handleKeyDown(e, campo.id)}
                                  placeholder="Nombre del campo..."
                                  required
                                />
                              </td>
                              <td>
                                <textarea
                                  className="form-control form-control-sm"
                                  value={tempFieldData.especificacion}
                                  onChange={(e) => setTempFieldData({...tempFieldData, especificacion: e.target.value})}
                                  onKeyDown={(e) => handleKeyDown(e, campo.id)}
                                  placeholder="Especificación adicional..."
                                  rows="2"
                                  style={{ resize: 'vertical' }}
                                />
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm">
                                  <button 
                                    className="btn btn-success"
                                    onClick={() => saveInlineEdit(campo.id)}
                                    disabled={savingField || !tempFieldData.nombre.trim()}
                                    title="Guardar cambios (Enter)"
                                  >
                                    {savingField ? (
                                      <span className="spinner-border spinner-border-sm"></span>
                                    ) : (
                                      <i className="bi bi-check"></i>
                                    )}
                                  </button>
                                  <button 
                                    className="btn btn-secondary"
                                    onClick={cancelInlineEdit}
                                    disabled={savingField}
                                    title="Cancelar (Escape)"
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            // Modo vista
                            <>
                              <td>
                                <span className="badge bg-primary">{campo.orden}</span>
                              </td>
                              <td>
                                <strong>{campo.nombre}</strong>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {campo.especificacion || <em className="text-muted">Sin especificación</em>}
                                </small>
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm">
                                  <button 
                                    className="btn btn-outline-primary"
                                    onClick={() => startInlineEdit(campo)}
                                    title="Editar inline (doble clic en la fila)"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button 
                                    className="btn btn-outline-danger"
                                    onClick={() => onDeleteField(campo.id)}
                                    title="Eliminar campo"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                    
                    {/* Fila para agregar nuevo campo */}
                    {addingNewField === doc.id && (
                      <tr className="table-warning">
                        <td>
                          <input
                            type="number"
                            className={`form-control form-control-sm ${
                              doc.campos.some(c => c.orden === tempFieldData.orden) ? 'is-invalid' : ''
                            }`}
                            value={tempFieldData.orden}
                            onChange={(e) => setTempFieldData(prev => ({
                              ...prev,
                              orden: parseInt(e.target.value) || 1
                            }))}
                            onKeyDown={(e) => handleKeyDown(e, doc.id, true)}
                            min="1"
                            title={`Órdenes ocupados: ${doc.campos.map(c => c.orden).sort((a,b) => a-b).join(', ')}`}
                          />
                          {doc.campos.some(c => c.orden === tempFieldData.orden) && (
                            <div className="invalid-feedback">
                              Orden {tempFieldData.orden} ya está en uso
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={tempFieldData.nombre}
                            onChange={(e) => setTempFieldData(prev => ({
                              ...prev,
                              nombre: e.target.value
                            }))}
                            onKeyDown={(e) => handleKeyDown(e, doc.id, true)}
                            placeholder="Nombre del campo"
                            autoFocus
                          />
                        </td>
                        <td>
                          <textarea
                            className="form-control form-control-sm"
                            value={tempFieldData.especificacion}
                            onChange={(e) => setTempFieldData(prev => ({
                              ...prev,
                              especificacion: e.target.value
                            }))}
                            onKeyDown={(e) => handleKeyDown(e, doc.id, true)}
                            placeholder="Especificación adicional para una mejor captura de datos (ej: 'Solo nombres y apellidos', 'Fecha en DD/MM/YYYY', etc.)"
                            rows="2"
                          />
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button 
                              className="btn btn-success"
                              onClick={() => saveNewField(doc.id)}
                              disabled={savingField || !tempFieldData.nombre}
                              title="Guardar nuevo campo"
                            >
                              <i className="bi bi-check"></i>
                            </button>
                            <button 
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                setAddingNewField(null);
                                setTempFieldData({});
                              }}
                              title="Cancelar"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Instrucciones de uso */}
                <div className="row">
                  <div className="col-md-5">
                    <div className="alert alert-info mt-2 py-2 px-3 small">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>Edición Rápida:</strong> Haz <strong>doble clic</strong> en cualquier fila para editar inline. Usa <kbd>Enter</kbd> para guardar o <kbd>Escape</kbd> para cancelar.
                    </div>
                  </div>
                  <div className="col-md-7">
                    <div className="alert alert-warning mt-2 py-2 px-3 small">
                      <i className="bi bi-lightbulb me-2"></i>
                      La <strong>Especificación</strong> se utiliza para formar el patrón de recuperación con el formato <code>Nombre del Campo (Especificación)</code>. Esto ayuda a darle más contexto y precisión a Gemini durante la extracción de datos.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentAccordion;
